import { Box, HStack, SimpleGrid, Text } from '@chakra-ui/react';
import { InternalIngestionFolderFragment } from '@client/graphql/__generated__/types';
import { FC, ReactNode, useMemo } from 'react';

type NullableFolder = InternalIngestionFolderFragment | null | undefined;

export const PipelineStageSelectionTooltipContent: FC = () => (
  <Box maxW="400px">
    <Text fontWeight="bold" mb={2}>
      Pipeline & Stage Selection Logic
    </Text>

    <Text fontSize="sm" fontWeight="bold" mb={1}>
      1. Target State Identification:
    </Text>
    <Text fontSize="xs" mb={2} pl={2}>
      • First: Document versions (most common state among classified docs)
      <br />
      • Fallback: Property records → MLS listings → Normalized contract fields
      <br />• Must find valid US state to proceed
    </Text>

    <Text fontSize="sm" fontWeight="bold" mb={1}>
      2. Pipeline Matching:
    </Text>
    <Text fontSize="xs" mb={2} pl={2}>
      Matches pipeline by 3 criteria:
      <br />• <strong>Client Type:</strong> Derived from folder transaction side
      (BUY→BUYER, SELL/BUY_SELL→SELLER)
      <br />• <strong>Property Type:</strong> From HouseCanary records or MLS
      (mapped to RESIDENTIAL/LAND/COMMERCIAL/RENTAL)
      <br />• <strong>US State:</strong> From step 1
      <br />
      <br />
      If no direct match: Falls back to evaluating pipeline rules against folder
      autofill data
    </Text>

    <Text fontSize="sm" fontWeight="bold" mb={1}>
      3. Stage Selection:
    </Text>
    <Text fontSize="xs" pl={2}>
      • Evaluates all stages in order (by sortOrder)
      <br />
      • Each stage has requirement rules that ALL must pass
      <br />
      • Rules check: autofill values, document existence, field values
      <br />• Selects LAST enabled stage (highest sortOrder where requirements
      pass)
    </Text>
  </Box>
);

interface PipelineStageSelectionInfoProps {
  folder?: NullableFolder;
}

const debugTextProps = {
  color: 'compliance.debug.text',
  fontSize: 'xs',
} as const;

const DebugText: FC<{ children: ReactNode; opacity?: number }> = ({
  children,
  opacity,
}) => (
  <Text {...debugTextProps} opacity={opacity}>
    {children}
  </Text>
);

const KeyValueRow: FC<{ label: string; value: ReactNode }> = ({
  label,
  value,
}) => (
  <HStack align="start" justify="space-between" spacing={3}>
    <DebugText opacity={0.7}>{label}:</DebugText>
    <Box maxW="60%" textAlign="right">
      {typeof value === 'string' ? <DebugText>{value}</DebugText> : value}
    </Box>
  </HStack>
);

const getStringValue = (value: unknown) =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

export const PipelineStageSelectionInfo: FC<
  PipelineStageSelectionInfoProps
> = ({ folder }) => {
  const pipeline = folder?.transactionPipeline;

  const metadata = useMemo(
    () =>
      folder?.metadata ? (folder.metadata as Record<string, unknown>) : {},
    [folder?.metadata]
  );

  const normalizedValues = useMemo(
    () =>
      folder?.normalizedFieldValues
        ? (folder.normalizedFieldValues as Record<string, unknown>)
        : {},
    [folder?.normalizedFieldValues]
  );

  const normalizedAddress =
    getStringValue(normalizedValues.property_full_address) ||
    getStringValue(metadata.property_full_address);

  const mlsFullAddress = useMemo(() => {
    const addr = folder?.mlsListing?.address;
    if (!addr?.full) {
      return 'MLS address not available';
    }

    const parts = [
      addr.full,
      addr.unit,
      addr.city,
      addr.state,
      addr.postalCode,
    ].filter(Boolean);

    return parts.join(', ');
  }, [folder?.mlsListing?.address]);

  if (!folder) {
    return <DebugText opacity={0.6}>Folder data unavailable</DebugText>;
  }

  return (
    <SimpleGrid columns={1} spacingY={1}>
      <KeyValueRow
        label="Pipeline Target State"
        value={pipeline?.usState || 'not available'}
      />
      <KeyValueRow
        label="Pipeline Property Type"
        value={pipeline?.transactionPropertyType || 'not available'}
      />
      <KeyValueRow label="MLS Address" value={mlsFullAddress} />
      <KeyValueRow
        label="MLS State"
        value={folder.mlsListing?.address?.state || 'not available'}
      />
      <KeyValueRow
        label="MLS Property Type"
        value={folder.mlsListing?.property?.type || 'not available'}
      />
      <KeyValueRow
        label="MLS Property Subtype"
        value={
          folder.mlsListing?.property?.subTypeText ||
          folder.mlsListing?.property?.subType ||
          'not available'
        }
      />
      <KeyValueRow
        label="MLS Listing ID"
        value={
          getStringValue(normalizedValues.mls_listing_id) ||
          folder.activeMlsListingId ||
          'not available'
        }
      />
      <KeyValueRow
        label="Normalized Address"
        value={normalizedAddress || 'not available'}
      />
      <KeyValueRow
        label="Normalized State"
        value={
          getStringValue(normalizedValues.property_state) || 'not available'
        }
      />
      <KeyValueRow
        label="Normalized Transaction Side"
        value={
          getStringValue(normalizedValues.transaction_side) || 'not available'
        }
      />
      <KeyValueRow
        label="Folder Transaction Side"
        value={folder.transactionSide || 'not set'}
      />
      <KeyValueRow
        label="Metadata Transaction Status"
        value={getStringValue(metadata.transaction_status) || 'not set'}
      />
      <KeyValueRow
        label="Normalized Status"
        value={folder.normalizedStatus || 'not set'}
      />
      <KeyValueRow
        label="Metadata Office"
        value={
          getStringValue(metadata.office_name) ||
          getStringValue(metadata.office_id) ||
          'not available'
        }
      />
    </SimpleGrid>
  );
};
